import { Box, useTheme } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { IconGripVertical } from '@tabler/icons-react';

/**
 * Drag between budget categories (#1964, DESIGN_SYSTEM.md section 8.16).
 *
 * Thin wrappers so the dnd-kit hooks live in components of their own; the page
 * owns the DndContext, the move, and the DragOverlay.
 */

/**
 * One Normal budget row, draggable for editors at all times. A hold lifts it
 * (the sensors' delay), so a tap still opens the budget. The wrapper is the
 * measured drag node and carries the row divider, because the Row inside it
 * is the wrapper's only child and its own `:last-child` rule would drop every
 * divider.
 *
 * On a hover pointer a grip box covers the row's icon box while the row is
 * hovered: the affordance. Touch has no hover, so the hold is the gesture.
 *
 * No keyboard attributes are spread: there is no keyboard sensor, and the Edit
 * budget dialog's Category select is the keyboard path.
 */
export const DraggableBudget = ({ id, gripSize = 'default', children }) => {
  const { palette, ds } = useTheme();
  const { setNodeRef, listeners, isDragging } = useDraggable({ id });
  const boxSize = gripSize === 'compact' ? ds.size.iconBoxCompact : ds.size.iconBox;
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      data-budget-draggable={id}
      sx={{
        position: 'relative',
        opacity: isDragging ? 0.4 : 1,
        // Hold-to-drag must not open the long-press callout or select text.
        WebkitTouchCallout: 'none',
        userSelect: 'none',
        '&:not(:last-child)': { borderBottom: `1px solid ${palette.divider}` },
        '& .br-drag-grip': { opacity: 0 },
        '@media (hover: hover)': {
          '&:hover .br-drag-grip': { opacity: 1 },
        },
      }}
    >
      {children}
      <Box
        className="br-drag-grip"
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: boxSize,
          height: boxSize,
          borderRadius: `${ds.radius.icon}px`,
          backgroundColor: palette.surface.raised,
          backgroundImage: `linear-gradient(${palette.tint.interactive}, ${palette.tint.interactive})`,
          color: palette.interactive,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
          cursor: 'grab',
          transition: `opacity ${ds.motion.fast} ${ds.motion.easeOut}`,
        }}
      >
        <IconGripVertical size={gripSize === 'compact' ? 14 : 16} />
      </Box>
    </Box>
  );
};

/**
 * One category card (Default included) as a drop target. `children` is a render
 * function given `isOver`, so the card can paint the drop-target highlight.
 */
export const DroppableCard = ({ id, disabled = false, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  return (
    <Box ref={setNodeRef} data-budget-drop={id} sx={{ minWidth: 0 }}>
      {children(isOver && !disabled)}
    </Box>
  );
};
